import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  FileText,
  Shield,
  Target,
  Link2,
  Plus,
  Download,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  Eye,
  Archive,
  X,
  ArrowRight,
  Layers,
  File,
  FileArchive,
  Settings,
  History,
  Check,
  Share2
} from 'lucide-react';
import ShareDashboardModal from '../components/ShareDashboardModal';
import AuditPackExportModal from '../components/AuditPackExportModal';
import { apiFetch } from '../services/api_service';


const API = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const PACK_STATUS = {
  draft: { label: 'Draft', color: 'slate', icon: FileText },
  generating: { label: 'Generating...', color: 'blue', icon: RefreshCw },
  ready: { label: 'Ready', color: 'green', icon: CheckCircle2 },
  expired: { label: 'Expired', color: 'amber', icon: AlertTriangle },
  error: { label: 'Error', color: 'red', icon: AlertTriangle }
};

// Template configurations
const TEMPLATE_ICONS = {
  csddd: { icon: Shield, color: 'purple' },
  csrd: { icon: FileText, color: 'blue' },
  eu_taxonomy: { icon: Target, color: 'green' },
  gri: { icon: Layers, color: 'red' },
  custom: { icon: Package, color: 'slate' }
};

const AuditPackBuilder = () => {
  const [packs, setPacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [availableItems, setAvailableItems] = useState({ obligations: [], claims: [] });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // New pack form
  const [newPackName, setNewPackName] = useState('');
  const [newPackTemplate, setNewPackTemplate] = useState('custom');
  const [newPackFormat, setNewPackFormat] = useState('both');
  const [newPackDescription, setNewPackDescription] = useState('');

  // Fetch packs and templates
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch templates
        const templatesRes = await fetch(`${API}/api/audit-packs/templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (templatesRes.ok) {
          const data = await templatesRes.json();
          if (data.success) setTemplates(data.templates || []);
        }
        
        // Fetch packs
        const packsRes = await fetch(`${API}/api/audit-packs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (packsRes.ok) {
          const data = await packsRes.json();
          if (data.success) setPacks(data.packs || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch available items for adding to pack
  const fetchAvailableItems = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch obligations
      const obsRes = await fetch(`${API}/api/disclosure/obligations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch claims
      const claimsRes = await fetch(`${API}/api/disclosure/claims`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const items = { obligations: [], claims: [] };
      
      if (obsRes.ok) {
        const data = await obsRes.json();
        items.obligations = data.obligations || [];
      }
      
      if (claimsRes.ok) {
        const data = await claimsRes.json();
        items.claims = data.claims || [];
      }
      
      setAvailableItems(items);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  // Create new pack
  const handleCreatePack = async () => {
    if (!newPackName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/audit-packs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newPackName,
          template: newPackTemplate,
          format: newPackFormat,
          description: newPackDescription
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPacks(prev => [data.pack, ...prev]);
          setSelectedPack(data.pack);
          setShowCreateModal(false);
          setNewPackName('');
          setNewPackDescription('');
        }
      }
    } catch (err) {
      console.error('Error creating pack:', err);
    }
  };

  // Add items to pack
  const handleAddItems = async (obligationIds, claimIds) => {
    if (!selectedPack) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/audit-packs/${selectedPack.id}/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          obligation_ids: obligationIds,
          claim_ids: claimIds
        })
      });
      
      if (response.ok) {
        // Refresh pack
        const packRes = await fetch(`${API}/api/audit-packs/${selectedPack.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (packRes.ok) {
          const data = await packRes.json();
          if (data.success) {
            setSelectedPack(data.pack);
            setPacks(prev => prev.map(p => p.id === data.pack.id ? data.pack : p));
          }
        }
        setShowAddItemsModal(false);
      }
    } catch (err) {
      console.error('Error adding items:', err);
    }
  };

  // Generate pack
  const handleGeneratePack = async () => {
    if (!selectedPack) return;
    
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/audit-packs/${selectedPack.id}/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh pack to get download URLs
          const packRes = await fetch(`${API}/api/audit-packs/${selectedPack.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (packRes.ok) {
            const packData = await packRes.json();
            if (packData.success) {
              setSelectedPack(packData.pack);
              setPacks(prev => prev.map(p => p.id === packData.pack.id ? packData.pack : p));
            }
          }
        }
      }
    } catch (err) {
      console.error('Error generating pack:', err);
    } finally {
      setGenerating(false);
    }
  };

  // Delete pack
  const handleDeletePack = async (packId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/audit-packs/${packId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPacks(prev => prev.filter(p => p.id !== packId));
        if (selectedPack?.id === packId) setSelectedPack(null);
      }
    } catch (err) {
      console.error('Error deleting pack:', err);
    }
  };

  // Download file
  const handleDownload = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="audit-pack-loading">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
        <span className="ml-3 text-slate-600">Loading Audit Pack Builder...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-pack-builder">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Package size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Audit Pack Builder</h1>
              <p className="text-amber-100">Bundle obligations, claims & evidence into audit-ready packages</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowShareModal(true)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 flex items-center gap-2 border border-white/30"
              data-testid="share-audit-pack-btn"
            >
              <Share2 size={18} />
              Share
            </button>
            <button 
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 flex items-center gap-2 border border-white/30"
              data-testid="export-audit-pack-btn"
            >
              <Download size={18} />
              Export PDF
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-white text-amber-700 rounded-lg font-semibold hover:bg-amber-50 flex items-center gap-2"
              data-testid="create-pack-btn"
            >
              <Plus size={18} />
              Create Pack
            </button>
          </div>
        </div>
      </div>

      {/* Workflow Reminder */}
      <div className="bg-slate-900 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">Defensibility Flywheel:</span>
            {['Upload', 'Map', 'Evidence', 'Claim', 'Audit Pack'].map((step, idx) => (
              <React.Fragment key={step}>
                <div className={`flex items-center gap-2 ${idx === 4 ? 'text-amber-400 font-semibold' : 'text-green-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    idx === 4 ? 'bg-amber-500 text-white' : 'bg-green-500/20 text-green-400'
                  }`}>
                    {idx < 4 ? <CheckCircle2 size={14} /> : idx + 1}
                  </div>
                  <span className="text-sm">{step}</span>
                </div>
                {idx < 4 && <ArrowRight size={16} className="text-slate-500" />}
              </React.Fragment>
            ))}
          </div>
          <span className="text-xs text-slate-500">Final step: Generate audit-ready packages</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pack List */}
        <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="pack-list">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Archive size={18} className="text-amber-600" />
              Your Audit Packs ({packs.length})
            </h2>
          </div>
          
          <div className="max-h-[600px] overflow-y-auto">
            {packs.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {packs.map(pack => {
                  const status = PACK_STATUS[pack.status] || PACK_STATUS.draft;
                  const template = TEMPLATE_ICONS[pack.template] || TEMPLATE_ICONS.custom;
                  const StatusIcon = status.icon;
                  const TemplateIcon = template.icon;
                  const isSelected = selectedPack?.id === pack.id;
                  
                  return (
                    <div 
                      key={pack.id}
                      onClick={() => setSelectedPack(pack)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        isSelected ? 'bg-amber-50 border-l-4 border-l-amber-500' : ''
                      }`}
                      data-testid={`pack-${pack.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${template.color}-100`}>
                          <TemplateIcon size={18} className={`text-${template.color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{pack.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs bg-${status.color}-100 text-${status.color}-700 flex items-center gap-1`}>
                              <StatusIcon size={10} />
                              {status.label}
                            </span>
                            <span className="text-xs text-slate-500">
                              {pack.total_obligations || 0} obs • {pack.total_claims || 0} claims
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={18} className="text-slate-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Package size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No audit packs yet</p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
                >
                  Create Your First Pack
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Pack Details */}
        <div className="lg:col-span-2 space-y-4">
          {selectedPack ? (
            <>
              {/* Pack Header */}
              <div className="bg-white rounded-xl shadow p-6" data-testid="pack-details">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedPack.name}</h2>
                    <p className="text-slate-500 mt-1">{selectedPack.description || 'No description'}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <span className={`px-3 py-1 rounded-full text-sm bg-${PACK_STATUS[selectedPack.status]?.color || 'slate'}-100 text-${PACK_STATUS[selectedPack.status]?.color || 'slate'}-700`}>
                        {PACK_STATUS[selectedPack.status]?.label || 'Unknown'}
                      </span>
                      <span className="text-sm text-slate-500">
                        Template: {templates.find(t => t.value === selectedPack.template)?.name || selectedPack.template}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedPack.status === 'draft' && (
                      <button 
                        onClick={() => { fetchAvailableItems(); setShowAddItemsModal(true); }}
                        className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-2"
                        data-testid="add-items-btn"
                      >
                        <Plus size={16} />
                        Add Items
                      </button>
                    )}
                    <button 
                      onClick={handleGeneratePack}
                      disabled={generating || selectedPack.items?.length === 0}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                      data-testid="generate-pack-btn"
                    >
                      {generating ? <RefreshCw size={16} className="animate-spin" /> : <Package size={16} />}
                      {generating ? 'Generating...' : 'Generate Pack'}
                    </button>
                  </div>
                </div>

                {/* Completeness Score */}
                {selectedPack.completeness_score > 0 && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Completeness Score</span>
                      <span className="text-lg font-bold text-amber-600">{selectedPack.completeness_score}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-amber-500 h-2 rounded-full transition-all"
                        style={{ width: `${selectedPack.completeness_score}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Download Buttons */}
                {selectedPack.status === 'ready' && (
                  <div className="mt-4 flex items-center gap-3">
                    {selectedPack.pdf_summary_url && (
                      <button 
                        onClick={() => handleDownload(selectedPack.pdf_summary_url, `${selectedPack.name}_Summary.pdf`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                        data-testid="download-pdf-btn"
                      >
                        <FileText size={16} />
                        Download PDF Summary
                      </button>
                    )}
                    {selectedPack.zip_bundle_url && (
                      <button 
                        onClick={() => handleDownload(selectedPack.zip_bundle_url, `${selectedPack.name}_Bundle.zip`)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                        data-testid="download-zip-btn"
                      >
                        <FileArchive size={16} />
                        Download ZIP Bundle
                      </button>
                    )}
                    {selectedPack.single_pdf_url && (
                      <button 
                        onClick={() => handleDownload(selectedPack.single_pdf_url, `${selectedPack.name}_Full.pdf`)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-2"
                        data-testid="download-full-pdf-btn"
                      >
                        <File size={16} />
                        Download Full PDF
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Pack Contents */}
              <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="pack-contents">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-bold text-slate-900">Pack Contents</h3>
                </div>
                
                {selectedPack.items?.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {/* Obligations */}
                    {selectedPack.items.filter(i => i.item_type === 'obligation').length > 0 && (
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Target size={16} className="text-purple-600" />
                          Obligations ({selectedPack.items.filter(i => i.item_type === 'obligation').length})
                        </h4>
                        <div className="space-y-2">
                          {selectedPack.items.filter(i => i.item_type === 'obligation').map(item => (
                            <div key={item.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div>
                                <span className="font-mono text-xs text-slate-500">{item.requirement_code}</span>
                                <p className="text-sm text-slate-700">{item.title}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {item.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Claims */}
                    {selectedPack.items.filter(i => i.item_type === 'claim').length > 0 && (
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Shield size={16} className="text-green-600" />
                          Claims ({selectedPack.items.filter(i => i.item_type === 'claim').length})
                        </h4>
                        <div className="space-y-2">
                          {selectedPack.items.filter(i => i.item_type === 'claim').map(item => (
                            <div key={item.id} className="p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-slate-700">{item.title}</p>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  item.status === 'approved' || item.status === 'published' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {item.status}
                                </span>
                              </div>
                              {item.evidence_ids?.length > 0 && (
                                <p className="text-xs text-slate-500 mt-1">
                                  📎 {item.evidence_ids.length} evidence item(s) linked
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evidence */}
                    {selectedPack.items.filter(i => i.item_type === 'evidence').length > 0 && (
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Link2 size={16} className="text-blue-600" />
                          Evidence ({selectedPack.items.filter(i => i.item_type === 'evidence').length})
                        </h4>
                        <div className="space-y-2">
                          {selectedPack.items.filter(i => i.item_type === 'evidence').map(item => (
                            <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                              <FileText size={14} className="text-slate-400" />
                              <span className="text-sm text-slate-700">{item.title}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Package size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No items in this pack yet</p>
                    <button 
                      onClick={() => { fetchAvailableItems(); setShowAddItemsModal(true); }}
                      className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
                    >
                      Add Items
                    </button>
                  </div>
                )}
              </div>

              {/* Pack Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{selectedPack.total_obligations || 0}</p>
                  <p className="text-xs text-slate-500">Obligations</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedPack.total_claims || 0}</p>
                  <p className="text-xs text-slate-500">Claims</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{selectedPack.total_evidence || 0}</p>
                  <p className="text-xs text-slate-500">Evidence</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{selectedPack.completeness_score || 0}%</p>
                  <p className="text-xs text-slate-500">Complete</p>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <Package size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Select or Create an Audit Pack</h3>
              <p className="text-slate-500">Choose a pack from the list or create a new one to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Pack Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-pack-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Create Audit Pack</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pack Name *</label>
                <input
                  type="text"
                  value={newPackName}
                  onChange={(e) => setNewPackName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g., CSDDD 2024 Audit Pack"
                  data-testid="pack-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template</label>
                <select
                  value={newPackTemplate}
                  onChange={(e) => setNewPackTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  data-testid="pack-template-select"
                >
                  {templates.map(t => (
                    <option key={t.value} value={t.value}>{t.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Output Format</label>
                <select
                  value={newPackFormat}
                  onChange={(e) => setNewPackFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  data-testid="pack-format-select"
                >
                  <option value="pdf_zip">PDF Summary + ZIP Bundle</option>
                  <option value="single_pdf">Single PDF with Embedded Evidence</option>
                  <option value="both">Both Options</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={newPackDescription}
                  onChange={(e) => setNewPackDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button 
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreatePack}
                disabled={!newPackName.trim()}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                data-testid="confirm-create-btn"
              >
                Create Pack
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Items Modal */}
      {showAddItemsModal && (
        <AddItemsModal
          availableItems={availableItems}
          existingItems={selectedPack?.items || []}
          onAdd={handleAddItems}
          onClose={() => setShowAddItemsModal(false)}
        />
      )}
    </div>
  );
};

// Add Items Modal Component
const AddItemsModal = ({ availableItems, existingItems, onAdd, onClose }) => {
  const [selectedObligations, setSelectedObligations] = useState([]);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const existingObIds = existingItems.filter(i => i.item_type === 'obligation').map(i => i.item_id);
  const existingClaimIds = existingItems.filter(i => i.item_type === 'claim').map(i => i.item_id);

  const filteredObligations = availableItems.obligations.filter(ob => {
    if (existingObIds.includes(ob.requirement_id)) return false;
    if (searchTerm && !ob.requirement?.title?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const filteredClaims = availableItems.claims.filter(claim => {
    if (existingClaimIds.includes(claim.id)) return false;
    if (searchTerm && !claim.title?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const toggleObligation = (id) => {
    setSelectedObligations(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleClaim = (id) => {
    setSelectedClaims(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-items-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Add Items to Pack</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Obligations */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <Target size={16} className="text-purple-600" />
              Obligations ({filteredObligations.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filteredObligations.map(ob => (
                <label key={ob.requirement_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={selectedObligations.includes(ob.requirement_id)}
                    onChange={() => toggleObligation(ob.requirement_id)}
                    className="rounded border-slate-300"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{ob.requirement?.title || ob.requirement_id}</p>
                    <p className="text-xs text-slate-500">{ob.requirement?.code} • {ob.status}</p>
                  </div>
                </label>
              ))}
              {filteredObligations.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No available obligations</p>
              )}
            </div>
          </div>
          
          {/* Claims */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <Shield size={16} className="text-green-600" />
              Claims ({filteredClaims.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filteredClaims.map(claim => (
                <label key={claim.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={selectedClaims.includes(claim.id)}
                    onChange={() => toggleClaim(claim.id)}
                    className="rounded border-slate-300"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{claim.title}</p>
                    <p className="text-xs text-slate-500">{claim.status} • {claim.category}</p>
                  </div>
                </label>
              ))}
              {filteredClaims.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No available claims</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-500">
            {selectedObligations.length + selectedClaims.length} item(s) selected
          </span>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </button>
            <button 
              onClick={() => onAdd(selectedObligations, selectedClaims)}
              disabled={selectedObligations.length + selectedClaims.length === 0}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              data-testid="confirm-add-items-btn"
            >
              Add Selected Items
            </button>
          </div>
        </div>
      </div>
      
      {/* Share Modal */}
      <ShareDashboardModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        dashboardType="audit_pack"
        dashboardTitle={selectedPack?.name || "Audit Pack"}
      />
      
      {/* Export Modal */}
      <AuditPackExportModal 
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
};

export default AuditPackBuilder;
